import useIdentity from "@/features/ui/hooks/use-identity";
import { Box, Button, Flex, Text, useToast } from "@chakra-ui/react";
import AppLineSheetTypeSelector from "@/features/line-sheet-sets/app-line-sheet-type-selector";
import AppCompanySelect from "@/features/line-sheet-sets/app-company-select";
import AppButton from "@/features/line-sheet-sets/app-button";
import AppPagination from "@/features/line-sheet-sets/app-pagination";
import {
  DEFAULT_LIST_LINE_SHEET_PAIRS_PAGE_SIZE
} from "@/features/line-sheet-sets/hooks/use-list-line-sheet-set-pairs";
import React, { useEffect, useMemo, useState } from "react";
import useListLineSheetSets, {
  ListLineSheetSetsPathParameter,
  ListLineSheetSetsRequest
} from "@/features/line-sheet-sets/hooks/use-list-line-sheet-sets";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import AppListHeader from "@/features/ui/app-list-header";
import { toTitle } from "@/utils/case";
import AppSeasonMultiSelect from "@/features/line-sheet-sets/app-season-multi-select";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";
import { OrderByItem } from "@/features/types";
import AgencyLineSheetSetGrid from "@/features/order-sheet-sets/agency-line-sheet-set-grid";
import useTypedSearchParams from "@/features/invoices/hooks/use-typed-search-params";
import useRefreshLineSheetSet from "@/features/order-sheet-sets/hooks/use-refresh-line-sheet-set";
import FetchLineSheetSetsFromEmailButton from "@/features/order-sheet-sets/fetch-line-sheet-sets-from-email-button";
import FilterModal from "@/features/order-sheet-sets/FilterModal";
import SVG from "react-inlinesvg";
import ChipList from "@/features/order-sheet-sets/ChipList";
import AppQueryControllerAutoComplete from "@/features/ui/app-query-controller-auto-complete";
import { Suggestion } from "@/features/ui/app-autocomplete-input";
import useGetCompanies from "@/features/order-sheet-sets/hooks/useGetCompanies";

export interface PersistedLineSheetSetListQuery
  extends Partial<ListLineSheetSetsRequest> {}

export default function AgencyLineSheetSetListPage() {
  const identity = useIdentity();
  const {
    error: showError,
    warning: showWarning,
    info: showInfo,
  } = useAppToasts({ id: "AgencyLineSheetSetListPage" });

  const [query, setQuery] =
    useTypedSearchParams<PersistedLineSheetSetListQuery>();

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [queryCount, setQueryCount] = useState(0);

  const { t, tTitle } = useI18nHelper();
  const { navigate } = useRouterHelper();
  const totalBrandList = useGetCompanies(["BRAND"]) ?? [];
  const totalBoutiqueList = useGetCompanies(["BOUTIQUE"]) ?? [];

  const toast = useToast();
  let inputValue: Suggestion;
  if (query?.query) {
    inputValue = {
      value: query.query,
      type: "custom",
      label: query.query,
    };
  } else {
    inputValue = {
      value: "",
      type: "custom",
      label: "",
    };
  }

  let suggestions: Suggestion[] = totalBrandList.map((brand) => ({
    type: "Brand",
    label: brand.name,
    value: brand.id.toString(),
    // 'EMPORIO ARMANI', '프라다' 같은 유사 표기로도 찾게 한다.
    keywords: brand.similarWords,
  }));
  suggestions = suggestions.concat(
    totalBoutiqueList.map((boutique) => ({
      type: "Boutique",
      label: boutique.name,
      value: boutique.id.toString(),
    }))
  );

  const listLineSheetSetsKey = useMemo(():
    | {
        parameter: ListLineSheetSetsPathParameter;
        request: ListLineSheetSetsRequest;
      }
    | undefined => {
    if (identity?.company) {
      return {
        parameter: {
          by: identity.company.type,
          companyId: identity.company.id,
        },
        request: {
          ...query,
          pageSize: DEFAULT_LIST_LINE_SHEET_PAIRS_PAGE_SIZE,
          pageNumber: query.pageNumber || 1,
          orderBy: [new OrderByItem("createdAt", "descend")],
        },
      };
    }
  }, [identity, query]);

  const {
    isLoading: isListLineSheetSetsLoading,
    data: listLineSheetSetsData,
    mutate: mutateListLineSheetSetsData,
    isValidating: isListLineSheetSetsValidating,
    error: listLineSheetSetsError,
  } = useListLineSheetSets(
    listLineSheetSetsKey?.parameter,
    listLineSheetSetsKey?.request,
    {
      shouldRetryOnError: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  useEffect(() => {
    if (listLineSheetSetsError) {
      showError(listLineSheetSetsError);
    }
  }, [showError, listLineSheetSetsError]);

  // useEffect(() => {
  //   return () => {
  //     mutateListLineSheetSetsData((v) => v, false);
  //   };
  // }, [mutateListLineSheetSetsData]);

  const [isRefreshing] = useRefreshLineSheetSet({
    data: listLineSheetSetsData,
    mutate: mutateListLineSheetSetsData,
  });

  return (
    <>
      <Flex
        flexDirection={"column"}
        gap={"12px"}
        height={"100%"}
        paddingBottom={"16px"}
      >
        <AppListHeader
          filters={[
            <AppQueryControllerAutoComplete
              key={"AgencyLineSheetSetListFilter_Query"}
              value={inputValue.type === "custom" ? inputValue : {
                value: '',
                type: 'custom',
                label: ''
              }}
              showButton={true}
              placeholder={t("search_input_placeholder")}
              leadingIcon="search"
              showIconsAndBtn={true}
              width="400px"
              suggestions={suggestions}
              onChange={(value) => {
                if (value.value && value.type !== "Brand" && value.type !== "Boutique" && value.value.length < 3) {
                  toast({
                    title: "검색어는 3글자 이상 입력해주세요.",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                    position: "top",
                  });
                  return;
                }
                setQuery((prev) => {
                  if (value.type === "Brand") {
                    // 이미 선택된 브랜드일 경우 무시
                    if (prev["madeByCompany.id__in"]?.map(
                      (id) => String(id)
                    ).includes(value.value)) {
                      return prev;
                    }
                    return {
                      ...prev,
                      "made_by_company.id__in": value.value,
                      query: "",
                      pageNumber: 1,
                    };
                  } else if (value.type === "Boutique") {
                    // 이미 선택된 경우 무시
                    if (prev["issuedByCompany.id__in"]?.map(
                      (id) => String(id)
                    ).includes(value.value)) {
                      return prev;
                    }
                    return {
                      ...prev,
                      "issued_by_company.id__in": value.value,
                      query: "",
                      pageNumber: 1,
                    };
                  } else {
                    return {
                      ...prev,
                      query: value.value,
                      pageNumber: 1,
                    };
                  }
                })
              }
              }
            />,
            <Button
              key={"FilterButton"}
              padding={"4px 8px"}
              height="28px"
              display="flex"
              gap={"4px"}
              variant={"outline"}
              onClick={() => setIsFilterModalOpen(true)}
              minWidth={"82px"}
              border={`${
                queryCount > 0 ? "1px solid #1272EF" : "1px solid #8F8F8C"
              }`}
            >
              <SVG
                src={"/icons-v1_1/filter.svg"}
                width={"16px"}
                height={"16px"}
                color={"#444444"}
                style={{
                  minWidth: "16px",
                }}
              />
              <Text textStyle={"body-sm-bold"}>
                {tTitle("filter")} {queryCount > 0 && queryCount}
              </Text>
            </Button>,
            () => {
              if (queryCount <= 0) return;
              return (
                <AppButton
                  key={"AgencyLineSheetSetListFilter_ResetFilter"}
                  variant={"ghost"}
                  size={"medium"}
                  onClick={() => {
                    setQuery({
                      pageNumber: 1,
                    });
                  }}
                  width={"72px"}
                  height={"28px"}
                >
                  <Flex gap={"4px"} alignItems={"center"}>
                    <SVG
                      src={"/icons-v1_1/arrow-rotate.svg"}
                      width={"16px"}
                      height={"16px"}
                      color={"#8F8F8C"}
                    />
                    <Text textStyle="body-sm-bold" color={"#444444"}>
                      {t("clear_all")}
                    </Text>
                  </Flex>
                </AppButton>
              );
            },
            () => {
              if (queryCount <= 0) return;
              return (
                <Text key={"Divider"} color={"#D4D4D1"}>
                  |
                </Text>
              );
            },
            <ChipList key={"ChipList"} query={query} setQuery={setQuery} />,
          ]}
          actions={[
            (() => {
              if (
                identity?.company?.type === "AGENCY" &&
                identity?.company?.id === 1 &&
                query.isDeleted__eq !== true
              ) {
                return (
                  <FetchLineSheetSetsFromEmailButton
                    key={"fetchLineSheetSetsFromEmail"}
                  />
                );
              }
              return undefined;
            })(),
            (() => {
              if (query.isDeleted__eq !== true) {
                return (
                  <AppButton
                    key={"AgencyLineSheetSetListAction__UploadLineSheetSets"}
                    variant={"primary"}
                    onClick={() => {
                      navigate("/line-sheet-sets/upload", {
                        appendReferer: true,
                      });
                    }}
                  >
                    {toTitle(
                      t("upload_line_sheet_sets", { ns: "line_sheet_sets" })
                    )}
                  </AppButton>
                );
              }
              return undefined;
            })(),
          ]}
          showFilterText={false}
        />
        <Box
          flexGrow={1}
          overflow={"auto"}
          style={{
            scrollbarGutter: "stable",
          }}
        >
          <AgencyLineSheetSetGrid
            isPendingLineSheetSetRefreshing={isRefreshing}
            resources={listLineSheetSetsError ? [] : listLineSheetSetsData?.lineSheetSets}
            mutateResources={mutateListLineSheetSetsData}
          />
        </Box>
        <FilterModal
          isOpen={isFilterModalOpen}
          onClose={() => setIsFilterModalOpen(false)}
          setQueryCount={(count) => setQueryCount(count)}
          query={query}
          setQuery={setQuery}
          resetBtn={
            <AppButton
              key={"AgencyLineSheetSetListFilter_ResetFilter"}
              variant={"ghost"}
              size={"medium"}
              onClick={() => {
                setQuery({
                  pageNumber: 1,
                });
              }}
              width={"72px"}
              height={"28px"}
            >
              <Flex gap={"4px"} alignItems={"center"}>
                <SVG
                  src={"/icons-v1_1/arrow-rotate.svg"}
                  width={"16px"}
                  height={"16px"}
                  color={"#8F8F8C"}
                />
                <Text textStyle="body-sm-bold" color={"#444444"}>
                  {t("clear_all")}
                </Text>
              </Flex>
            </AppButton>
          }
        >
          <Box>
            <Flex flexDirection="column" gap={"24px"}>
              <Flex flexDirection={"column"} gap={"6px"}>
                <Text>{tTitle("type")}</Text>
                <AppLineSheetTypeSelector
                  key={"AgencyLineSheetSetListFilter_Type"}
                  value={query.type__eq}
                  onSelect={(value) => {
                    setQuery((prev) => {
                      return {
                        ...prev,
                        type__eq: value,
                        pageNumber: 1,
                      };
                    });
                  }}
                />
              </Flex>
              <Flex flexDirection={"column"} gap={"6px"}>
                <Text>{tTitle("season")}</Text>
                <AppSeasonMultiSelect
                  key={"season"}
                  value={query.season__in}
                  onSelect={(value) => {
                    setQuery((prev) => {
                      return {
                        ...prev,
                        season__in: value,
                        pageNumber: 1,
                      };
                    });
                  }}
                />
              </Flex>
              <Flex flexDirection={"column"} gap={"6px"}>
                <Text>{tTitle("brand")}</Text>
                <AppCompanySelect
                  key={"AgencyLineSheetSetListFilter_Brand"}
                  name={toTitle(t("common:brand"))}
                  types={["BRAND"]}
                  id={query["madeByCompany.id__in"]}
                  isMultiple={true}
                  onSelect={(companies) => {
                    setQuery((prev) => {
                      return {
                        ...prev,
                        "madeByCompany.id__in": companies?.map(
                          (company) => company.id
                        ),
                        pageNumber: 1,
                      };
                    });
                  }}
                />
              </Flex>
              <Flex flexDirection={"column"} gap={"6px"}>
                <Text>{tTitle("issued_by")}</Text>
                <AppCompanySelect
                  key={"AgencyLineSheetSetListFilter_IssuedBy"}
                  name={toTitle(t("issued_by"))}
                  types={["BOUTIQUE"]}
                  id={query["issuedByCompany.id__in"]}
                  isMultiple={true}
                  onSelect={(companies) => {
                    setQuery((prev) => {
                      return {
                        ...prev,
                        "issuedByCompany.id__in": companies?.map(
                          (company) => company.id
                        ),
                        pageNumber: 1,
                      };
                    });
                  }}
                />
              </Flex>
            </Flex>
          </Box>
        </FilterModal>
        <Flex flexDirection={"row"} justifyContent={"center"}>
          <AppPagination
            capacity={10}
            pageSize={DEFAULT_LIST_LINE_SHEET_PAIRS_PAGE_SIZE}
            totalCount={listLineSheetSetsData?.totalCount}
            pageNumber={query.pageNumber}
            setPageNumber={(pageNumber) =>
              setQuery((prev) => {
                return {
                  ...prev,
                  pageNumber,
                };
              })
            }
          />
        </Flex>
      </Flex>
    </>
  );
}
